import React, { useMemo, useState, useEffect } from 'react';
import { DndContext, DragOverlay, closestCenter, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
    Cards,
    Plus,
    User,
    MapPin,
    Package,
    Image as ImageIcon,
    DotsThree,
    MagnifyingGlass,
    SortAscending,
    CaretDown,
    Circle,
    CircleDashed,
    Sparkle,
    FilmStrip,
    UsersThree,
    Cube,
    DotsSix,
    Notepad,
    Article,
    SquaresFour
} from '@phosphor-icons/react';
import { ProductionBar } from '../ProductionBar';
import { useIdea } from '../../context/IdeaContext';
import { useProject } from '../../context/ProjectContext';
import { useProjectPermissions } from '../../hooks/useProjectPermissions';
import { IdeaCard } from './IdeaCard';
import { OutlineStrip } from './OutlineStrip';
import { DynamicsPanel } from './DynamicsPanel';
import { CardInspector } from './CardInspector';
import { DraggableEntity } from './EntityChip';
import { BeatBoard } from './BeatBoard';
import { IdeaCard as IdeaCardType, AttachedEntity, CardFocus } from '../../types';

// ═══════════════════════════════════════════════════════════════════════════════
// COLOR PALETTE (Premiere Pro inspired, using project colors)
// ═══════════════════════════════════════════════════════════════════════════════
// Carbon Black: #1E1E20 - Main panel backgrounds
// Onyx: #121212 - Deep backgrounds, cards
// Graphite: #2A2A2C - Elevated surfaces, hover states
// Iron Grey: #3F4448 - Borders, dividers
// Medium Slate Blue: #7775F8 - Primary accent
// Mint Glow: #74F77D - Success/positive states

// Focus type config with STATIC Tailwind classes (dynamic strings don't work)
// Using muted, desaturated colors for premium feel
const FOCUS_CONFIG: Record<CardFocus, {
    label: string;
    icon: React.ReactNode;
    badgeBg: string;
    badgeBorder: string;
    badgeText: string;
    iconColor: string;
    stripeColor: string;
}> = {
    note: {
        label: 'Note',
        icon: <Cards className="w-3 h-3" />,
        badgeBg: 'bg-iron/40',
        badgeBorder: 'border-iron/60',
        badgeText: 'text-gray-400',
        iconColor: 'text-gray-500',
        stripeColor: 'bg-iron'
    },
    scene: {
        label: 'Scene',
        icon: <FilmStrip className="w-3 h-3" />,
        badgeBg: 'bg-place/10',
        badgeBorder: 'border-place/25',
        badgeText: 'text-place-soft',
        iconColor: 'text-place',
        stripeColor: 'bg-place'
    },
    character: {
        label: 'Character',
        icon: <User className="w-3 h-3" />,
        badgeBg: 'bg-entity/10',
        badgeBorder: 'border-entity/25',
        badgeText: 'text-entity-soft',
        iconColor: 'text-entity',
        stripeColor: 'bg-entity'
    },
    relationship: {
        label: 'Relationship',
        icon: <UsersThree className="w-3 h-3" />,
        badgeBg: 'bg-relationship/10',
        badgeBorder: 'border-relationship/25',
        badgeText: 'text-relationship-soft',
        iconColor: 'text-relationship',
        stripeColor: 'bg-relationship'
    },
    prop: {
        label: 'Prop',
        icon: <Cube className="w-3 h-3" />,
        badgeBg: 'bg-thing/10',
        badgeBorder: 'border-thing/25',
        badgeText: 'text-thing-soft',
        iconColor: 'text-thing',
        stripeColor: 'bg-thing'
    },
    prop_significance: {
        label: 'Significance',
        icon: <Sparkle className="w-3 h-3" />,
        badgeBg: 'bg-significance/10',
        badgeBorder: 'border-significance/25',
        badgeText: 'text-significance-soft',
        iconColor: 'text-significance',
        stripeColor: 'bg-significance'
    }
};

type StageView = 'card' | 'board' | 'prose';

const STAGE_VIEW_STORAGE_KEY = 'ezart.idea.stageView';

const readStoredStageView = (): StageView => {
    try {
        const stored = localStorage.getItem(STAGE_VIEW_STORAGE_KEY);
        if (stored === 'card' || stored === 'board' || stored === 'prose') return stored;
    } catch {
        // Storage unavailable; fall back to the card view
    }
    return 'card';
};

export const IdeaWorkspace: React.FC = () => {
    const {
        cards,
        workspaceState,
        createCard,
        updateCard,
        deleteCard,
        selectCard,
        attachEntity,
        detachEntity,
        placeCard,
        updateInstance,
        reorderInstances,
        currentIdea,
        loadIdeaByProjectId,
        createIdea,
        isLoading,
        characterArcs
    } = useIdea();

    const { selectedProjectId, projects } = useProject();
    const { canEdit } = useProjectPermissions(selectedProjectId || undefined);

    const [activeDragItem, setActiveDragItem] = React.useState<{
        type: 'card' | 'entity' | 'new-entity' | 'instance';
        data: any;
    } | null>(null);

    // Card flip state for inspector
    const [isCardFlipped, setIsCardFlipped] = useState(false);

    // Entity dock search
    const [entitySearch, setEntitySearch] = useState('');

    // Filter state
    const [filterScope, setFilterScope] = useState<'all' | 'unplaced' | 'macro' | 'micro'>('all');
    const [filterFocus, setFilterFocus] = useState<CardFocus | 'all'>('all');

    // View mode: 'card' (index card), 'board' (Beat Board canvas) or 'prose' (freeform writing)
    const [viewMode, setViewMode] = useState<StageView>(readStoredStageView);

    useEffect(() => {
        try {
            localStorage.setItem(STAGE_VIEW_STORAGE_KEY, viewMode);
        } catch {
            // Storage unavailable; the view simply isn't remembered
        }
    }, [viewMode]);


    // Get selected card
    const selectedCard = useMemo(() => {
        if (!workspaceState.selectedCardId) return null;
        return cards.find(c => c.id === workspaceState.selectedCardId) || null;
    }, [cards, workspaceState.selectedCardId]);

    // Check if dragging an entity (for dimming UI)
    const isDraggingEntity = activeDragItem?.type === 'new-entity';


    // Load or create idea when project changes
    useEffect(() => {
        const initIdea = async () => {
            if (!selectedProjectId) return;
            if (currentIdea?.project_id === selectedProjectId) return;

            const existingIdea = await loadIdeaByProjectId(selectedProjectId);

            if (!existingIdea) {
                const project = projects.find(p => p.id === selectedProjectId);
                await createIdea(project?.title || 'Untitled Idea', selectedProjectId);
            }
        };

        initIdea();
    }, [selectedProjectId, currentIdea?.project_id, loadIdeaByProjectId, createIdea, projects]);

    // Reset flip when card changes
    useEffect(() => {
        setIsCardFlipped(false);
    }, [selectedCard?.id]);

    // Handle drag start
    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const data = active.data.current;
        if (data) {
            setActiveDragItem({ type: data.type, data });
        }
    };

    // Handle drag end
    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDragItem(null);

        if (!over) return;

        const activeData = active.data.current;
        const overData = over.data.current;

        if (!activeData || !overData) return;

        // Entity dropped on card → attach
        if (activeData.type === 'new-entity' && overData.type === 'card-drop') {
            const entity = activeData.entity as AttachedEntity;
            const cardId = overData.cardId;
            await attachEntity(cardId, entity.type, entity.ref_id);
        }

        // Card dropped on an outline lane → place it at the page under the pointer and deselect
        if (activeData.type === 'card' && overData.type === 'timeline-lane') {
            const card = activeData.card as IdeaCardType;
            const lane = overData.lane as 'macro' | 'micro';
            const instance = await placeCard(card.id, lane);
            const dropped = active.rect.current.translated;
            if (instance && dropped && typeof overData.pageAt === 'function') {
                await updateInstance(instance.id, {
                    page_goal: overData.pageAt(dropped.left + dropped.width / 2),
                    page_length: overData.defaultLength(currentIdea?.outline_target_pages ?? 120)
                });
            }
            // Close the card viewer after placing
            selectCard(null);
        }

        // Instance reordering (when sortable drag ends)
        if (activeData.type === 'instance' && active.id !== over.id) {
            const activeInstance = activeData.instance;
            const lane = activeInstance.lane as 'macro' | 'micro';

            // Get all instances in this lane, sorted by order
            const laneInstances = cards
                .flatMap(c => c.instances || [])
                .filter(i => i.lane === lane)
                .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

            // Extract just the instance ID from the sortable IDs (format: "instance-{uuid}")
            const activeInstanceId = String(active.id).replace('instance-', '');
            const overInstanceId = String(over.id).replace('instance-', '');

            // Find indices
            const oldIndex = laneInstances.findIndex(i => i.id === activeInstanceId);
            const newIndex = laneInstances.findIndex(i => i.id === overInstanceId);

            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                // Reorder the array
                const reorderedInstances = [...laneInstances];
                const [removed] = reorderedInstances.splice(oldIndex, 1);
                reorderedInstances.splice(newIndex, 0, removed);

                // Get the new order of instance IDs
                const orderedIds = reorderedInstances.map(i => i.id);

                // Update the order
                await reorderInstances(lane, orderedIds);
            }
        }
    };

    // Get placement summary for selected card
    const getPlacementSummary = (card: IdeaCardType) => {
        if (!card.instances || card.instances.length === 0) return 'Unplaced';
        const macroCount = card.instances.filter(i => i.lane === 'macro').length;
        const microCount = card.instances.filter(i => i.lane === 'micro').length;
        const parts = [];
        if (macroCount > 0) parts.push(`${macroCount} macro`);
        if (microCount > 0) parts.push(`${microCount} micro`);
        return parts.join(' · ');
    };

    // Filter entities for dock search
    const filteredCharacters = useMemo(() => {
        if (!characterArcs) return [];
        const search = entitySearch.toLowerCase();
        return characterArcs.filter(c =>
            c.character?.name?.toLowerCase().includes(search)
        );
    }, [characterArcs, entitySearch]);

    // Show loading state
    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center bg-onyx">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-6 h-6 border-2 border-slate-blue/60 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs text-gray-500">Loading idea...</span>
                </div>
            </div>
        );
    }

    // Show "select project" state if no project is selected
    if (!selectedProjectId) {
        return (
            <div className="flex-1 flex items-center justify-center bg-onyx">
                <div className="text-center max-w-sm">
                    <Cards className="w-10 h-10 text-iron mx-auto mb-3" />
                    <p className="text-sm text-gray-400 mb-1.5">No project selected</p>
                    <p className="text-xs text-gray-500">
                        Select a project from the sidebar to start working on your story ideas.
                    </p>
                </div>
            </div>
        );
    }

    const focusConfig = selectedCard ? FOCUS_CONFIG[selectedCard.focus] : null;

    return (
        <DndContext
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            {/* Main container - Premiere Pro inspired dark UI */}
            <div className="flex-1 w-full flex flex-col h-full bg-carbon overflow-hidden relative">
                {/* Production Bar */}
                <ProductionBar page="idea" />

                {/* ═══════════════════════════════════════════════════════════════════
                    TOP SECTION: Entity Dock + Card Stage + Dynamics Panel
                    ═══════════════════════════════════════════════════════════════════ */}
                <div className="flex-1 flex overflow-hidden min-h-0 p-2 gap-2">
                    {/* LEFT: Entity Dock - Solid Panel (Premiere style) */}
                    <div className="w-52 flex-shrink-0 rounded-md overflow-hidden border border-iron/50 bg-neutral-800">
                        <EntityDock
                            characters={filteredCharacters}
                            searchValue={entitySearch}
                            onSearchChange={setEntitySearch}
                            isDimmed={false}
                            canEdit={canEdit}
                        />
                    </div>

                    {/* CENTER: Card Stage Area - Main workspace (Premiere style) */}
                    <div
                        className={`flex-1 flex flex-col rounded-md overflow-hidden relative transition-all duration-200 border border-iron/50 bg-onyx ${isDraggingEntity && !selectedCard ? 'opacity-60' : ''
                            }`}
                    >
                        {/* Card Stage Header - Clean, minimal */}
                        <div className="flex items-center justify-between px-3 py-2 border-b border-iron/40 bg-zinc-900">
                            {/* Left: View Mode Toggle */}
                            <div className="flex items-center gap-0.5 p-0.5 rounded bg-onyx border border-iron/40">
                                <button
                                    onClick={() => setViewMode('card')}
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-medium transition-colors ${
                                        viewMode === 'card'
                                            ? 'bg-graphite text-neutral-200'
                                            : 'text-gray-500 hover:text-gray-400'
                                    }`}
                                    title="Index Card View"
                                >
                                    <Notepad className="w-3 h-3" />
                                    <span>Card</span>
                                </button>
                                <button
                                    onClick={() => setViewMode('board')}
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-medium transition-colors ${
                                        viewMode === 'board'
                                            ? 'bg-graphite text-neutral-200'
                                            : 'text-gray-500 hover:text-gray-400'
                                    }`}
                                    title="Beat Board"
                                >
                                    <SquaresFour className="w-3 h-3" />
                                    <span>Board</span>
                                </button>
                                <button
                                    onClick={() => setViewMode('prose')}
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-medium transition-colors ${
                                        viewMode === 'prose'
                                            ? 'bg-graphite text-neutral-200'
                                            : 'text-gray-500 hover:text-gray-400'
                                    }`}
                                    title="Prose View"
                                >
                                    <Article className="w-3 h-3" />
                                    <span>Prose</span>
                                </button>
                            </div>

                            {/* Right: Focus badge + Placement status */}
                        {selectedCard && focusConfig ? (
                            <div className="flex items-center gap-2">
                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded ${focusConfig.badgeBg} border ${focusConfig.badgeBorder}`}>
                                    <span className={focusConfig.iconColor}>
                                        {focusConfig.icon}
                                    </span>
                                    <span className={`text-[10px] font-medium ${focusConfig.badgeText}`}>
                                        {focusConfig.label}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded bg-graphite border border-iron/40">
                                    {selectedCard.instances && selectedCard.instances.length > 0 ? (
                                        <>
                                            <Circle className="w-2 h-2 text-significance" weight="fill" />
                                            <span className="text-gray-400">{getPlacementSummary(selectedCard)}</span>
                                        </>
                                    ) : (
                                        <>
                                            <CircleDashed className="w-2 h-2 text-thing" />
                                            <span className="text-thing/80">Unplaced</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-[11px] text-gray-500">
                                Select or create a card to begin
                            </div>
                        )}
                        </div>

                        {/* Card Stage Content - Main work area */}
                        <div className="flex-1 flex items-center justify-center p-4 relative bg-zinc-950 min-h-0 overflow-hidden">
                            {/* Subtle grid pattern like Premiere */}
                            <div
                                className="absolute inset-0 opacity-[0.03]"
                                style={{
                                    backgroundImage: 'linear-gradient(to right, var(--color-iron) 1px, transparent 1px), linear-gradient(to bottom, var(--color-iron) 1px, transparent 1px)',
                                    backgroundSize: '20px 20px'
                                }}
                            />
                            {viewMode === 'board' ? (
                                <BeatBoard
                                    canEdit={canEdit}
                                    onOpenCard={(cardId) => {
                                        selectCard(cardId);
                                        setViewMode('card');
                                    }}
                                />
                            ) : viewMode === 'prose' ? (
                                /* Prose View - Full-width writing area for story outline */
                                <ProseView />
                            ) : selectedCard ? (
                                <FlippableCard
                                    card={selectedCard}
                                    isFlipped={isCardFlipped}
                                    isDraggingEntity={isDraggingEntity}
                                    focusConfig={focusConfig!}
                                    onFlip={() => setIsCardFlipped(!isCardFlipped)}
                                    onUpdate={canEdit ? (updates) => updateCard(selectedCard.id, updates) : undefined}
                                    onDelete={canEdit ? () => deleteCard(selectedCard.id) : undefined}
                                    onEntityRemove={canEdit ? (entityId) => detachEntity(entityId, selectedCard.id) : undefined}
                                />
                            ) : (
                                <EmptyCardState onCreateCard={canEdit ? createCard : undefined} hasCards={cards.length > 0} />
                            )}
                        </div>

                        {/* Card Stage Footer - Quick drag chips */}
                        <div className="flex items-center justify-center px-3 py-2 border-t border-iron/40 bg-zinc-900">
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-medium text-gray-500 uppercase tracking-wider mr-1">Quick Add</span>
                                <QuickDragChip type="subject" label="Character" icon={<User className="w-3 h-3" weight="fill" />} color="entity" />
                                <QuickDragChip type="location" label="Location" icon={<MapPin className="w-3 h-3" weight="fill" />} color="place" />
                                <QuickDragChip type="object" label="Prop" icon={<Package className="w-3 h-3" weight="fill" />} color="thing" />
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: Dynamics Panel - Solid panel (Premiere style) */}
                    <div className="w-56 flex-shrink-0 rounded-md overflow-hidden border border-iron/50 bg-neutral-800 flex flex-col">
                        <div className="px-3 py-2 border-b border-iron/40 bg-zinc-900">
                            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                                Dynamics
                            </span>
                        </div>
                        <div className="flex-1 overflow-y-auto custom-scrollbar">
                            <DynamicsPanel />
                        </div>
                    </div>
                </div>

                {/* ═══════════════════════════════════════════════════════════════════
                    MIDDLE: Command Strip (Search + Filters + Sort) - Premiere toolbar style
                    ═══════════════════════════════════════════════════════════════════ */}
                <div className="mx-2 mb-2 rounded-md border border-iron/50 bg-neutral-800 px-3 py-1.5 flex items-center gap-3">
                    {/* Search */}
                    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-zinc-900 border border-iron/40 hover:border-iron transition-colors flex-shrink-0 w-56">
                        <MagnifyingGlass className="w-3.5 h-3.5 text-gray-500" />
                        <input
                            type="text"
                            placeholder="Search cards..."
                            className="flex-1 bg-transparent text-xs text-neutral-200 placeholder-gray-500 outline-none"
                        />
                    </div>

                    <div className="w-px h-5 bg-iron/50" />

                    {/* Scope Pills - Premiere tab style */}
                    <div className="flex items-center gap-0.5 p-0.5 rounded bg-zinc-900 border border-iron/40">
                        {(['all', 'unplaced', 'macro', 'micro'] as const).map(scope => (
                            <button
                                key={scope}
                                onClick={() => setFilterScope(scope)}
                                className={`px-2.5 py-1 rounded-sm text-[10px] font-medium uppercase tracking-wide transition-colors ${filterScope === scope
                                    ? 'bg-graphite text-neutral-200'
                                    : 'text-gray-500 hover:text-gray-400 hover:bg-graphite/50'
                                    }`}
                            >
                                {scope === 'all' ? 'All' : scope === 'unplaced' ? 'Unplaced' : scope}
                            </button>
                        ))}
                    </div>

                    {/* Focus Filter Dropdown */}
                    <div className="relative group">
                        <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-zinc-900 border border-iron/40 text-[10px] font-medium text-gray-400 hover:text-neutral-200 hover:border-iron transition-colors">
                            <span>Focus: <span className="text-neutral-200">{filterFocus === 'all' ? 'All Types' : FOCUS_CONFIG[filterFocus].label}</span></span>
                            <CaretDown className="w-3 h-3 text-gray-500" />
                        </button>
                    </div>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Sort */}
                    <button className="flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-medium text-gray-500 hover:text-gray-400 hover:bg-graphite/50 transition-colors">
                        <SortAscending className="w-3.5 h-3.5" />
                        <span>Sort: Recent</span>
                    </button>

                    {/* Card count */}
                    <span className="px-2 py-0.5 rounded bg-zinc-900 border border-iron/40 text-[10px] font-medium text-gray-500">
                        {cards.length}
                    </span>

                    <div className="w-px h-5 bg-iron/50" />

                    {/* New Card Button - Only for editors */}
                    {canEdit && (
                        <button
                            onClick={() => createCard()}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-blue/15 border border-slate-blue/30 text-[11px] font-medium text-slate-blue-soft hover:bg-slate-blue/25 hover:border-slate-blue/40 transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" weight="bold" />
                            New Card
                        </button>
                    )}
                </div>

                {/* ═══════════════════════════════════════════════════════════════════
                    BOTTOM: Dual Timeline - Premiere timeline style
                    ═══════════════════════════════════════════════════════════════════ */}
                <div className="mx-2 mb-2 rounded-md overflow-hidden border border-iron/50 bg-neutral-800 flex-shrink-0 h-[300px]">
                    <OutlineStrip canEdit={canEdit} />
                </div>
            </div>

            {/* Drag Overlay - Subtle, refined */}
            <DragOverlay>
                {activeDragItem?.type === 'card' && activeDragItem.data.card && (
                    <div className="opacity-90 rotate-1 scale-[0.98]">
                        <IdeaCard
                            card={activeDragItem.data.card}
                            isCompact
                            isDraggable={false}
                            isDropTarget={false}
                        />
                    </div>
                )}
                {activeDragItem?.type === 'new-entity' && activeDragItem.data.entity && (
                    <div className="px-2.5 py-1.5 rounded bg-slate-blue/20 border border-slate-blue/40 text-slate-blue-soft text-xs font-medium shadow-lg">
                        {activeDragItem.data.entity.name}
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// QUICK DRAG CHIP - Premiere style entity shortcuts
// ═══════════════════════════════════════════════════════════════════════════════
interface QuickDragChipProps {
    type: 'subject' | 'location' | 'object';
    label: string;
    icon: React.ReactNode;
    color: 'entity' | 'place' | 'thing';
}

const QuickDragChip: React.FC<QuickDragChipProps> = ({ type, label, icon, color }) => {
    // Muted, desaturated colors for premium feel
    const colorClasses = {
        entity: 'bg-entity/8 border-entity/20 text-entity-soft hover:bg-entity/15 hover:border-entity/30',
        place: 'bg-place/8 border-place/20 text-place-soft hover:bg-place/15 hover:border-place/30',
        thing: 'bg-thing/8 border-thing/20 text-thing-soft hover:bg-thing/15 hover:border-thing/30'
    };

    return (
        <button
            className={`flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-medium transition-colors cursor-grab active:cursor-grabbing ${colorClasses[color]}`}
            title={`Drag to add ${label}`}
        >
            {icon}
            <span>{label}</span>
        </button>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ENTITY DOCK - Premiere Pro panel style
// ═══════════════════════════════════════════════════════════════════════════════
interface EntityDockProps {
    characters: Array<{ id: string; character_id: string; character?: { id: string; name: string } }>;
    searchValue: string;
    onSearchChange: (value: string) => void;
    isDimmed: boolean;
    canEdit: boolean;
}

const EntityDock: React.FC<EntityDockProps> = ({ characters, searchValue, onSearchChange, isDimmed, canEdit }) => {
    return (
        <div className={`flex flex-col h-full transition-opacity ${isDimmed ? 'opacity-40' : ''}`}>
            {/* Header - Premiere panel header style */}
            <div className="px-3 py-2 border-b border-iron/40 bg-zinc-900">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                    Entities
                </span>
            </div>

            {/* Search - Subtle input */}
            <div className="p-2 border-b border-iron/30">
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-zinc-900 border border-iron/40 hover:border-iron transition-colors">
                    <MagnifyingGlass className="w-3.5 h-3.5 text-gray-500" />
                    <input
                        type="text"
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Filter entities..."
                        className="flex-1 bg-transparent text-xs text-neutral-200 placeholder-gray-500 outline-none"
                    />
                </div>
            </div>

            {/* Entity Lists - Clean sections */}
            <div className="flex-1 overflow-y-auto p-2 space-y-4 custom-scrollbar">
                {/* Characters Section */}
                <div>
                    <div className="flex items-center justify-between mb-1.5 px-1">
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Characters</span>
                        <span className="text-[9px] text-gray-500 bg-graphite px-1.5 py-0.5 rounded min-w-[1.25rem] text-center">
                            {characters.length}
                        </span>
                    </div>

                    <div className="space-y-0.5">
                        {characters.length > 0 ? (
                            characters.slice(0, 10).map(char => (
                                <DraggableEntity
                                    key={char.id}
                                    entity={{
                                        id: `new-${char.character_id}`,
                                        type: 'subject',
                                        ref_id: char.character_id,
                                        name: char.character?.name || 'Unnamed'
                                    }}
                                />
                            ))
                        ) : (
                            <div className="px-2.5 py-3 rounded border border-dashed border-iron/40 bg-zinc-900/50 text-center">
                                <p className="text-[10px] text-gray-500">No characters found</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Locations Section */}
                <div>
                    <div className="flex items-center justify-between mb-1.5 px-1">
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Locations</span>
                        <span className="text-[9px] text-gray-500 bg-graphite px-1.5 py-0.5 rounded min-w-[1.25rem] text-center">
                            0
                        </span>
                    </div>
                    <div className="px-2.5 py-3 rounded border border-dashed border-iron/40 bg-zinc-900/50 text-center">
                        <p className="text-[10px] text-gray-500">No locations created</p>
                    </div>
                </div>

                {/* Props Section */}
                <div>
                    <div className="flex items-center justify-between mb-1.5 px-1">
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Props</span>
                        <span className="text-[9px] text-gray-500 bg-graphite px-1.5 py-0.5 rounded min-w-[1.25rem] text-center">
                            0
                        </span>
                    </div>
                    <div className="px-2.5 py-3 rounded border border-dashed border-iron/40 bg-zinc-900/50 text-center">
                        <p className="text-[10px] text-gray-500">No props created</p>
                    </div>
                </div>
            </div>

            {/* Create New Buttons - Only for editors */}
            {canEdit && (
                <div className="p-2 border-t border-iron/30 space-y-1.5 bg-zinc-900">
                    <button className="w-full h-7 flex items-center justify-center gap-1.5 rounded text-[10px] font-medium text-entity-soft bg-entity/8 hover:bg-entity/15 border border-entity/20 hover:border-entity/30 transition-colors">
                        <Plus className="w-3 h-3" />
                        New Character
                    </button>
                    <div className="flex gap-1.5">
                        <button className="flex-1 h-7 flex items-center justify-center gap-1.5 rounded text-[10px] font-medium text-place-soft bg-place/8 hover:bg-place/15 border border-place/20 hover:border-place/30 transition-colors">
                            <Plus className="w-3 h-3" />
                            Location
                        </button>
                        <button className="flex-1 h-7 flex items-center justify-center gap-1.5 rounded text-[10px] font-medium text-thing-soft bg-thing/8 hover:bg-thing/15 border border-thing/20 hover:border-thing/30 transition-colors">
                            <Plus className="w-3 h-3" />
                            Prop
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// FLIPPABLE CARD COMPONENT - Premiere Pro inspired styling
// ═══════════════════════════════════════════════════════════════════════════════
interface FlippableCardProps {
    card: IdeaCardType;
    isFlipped: boolean;
    isDraggingEntity: boolean;
    focusConfig: typeof FOCUS_CONFIG[CardFocus];
    onFlip: () => void;
    onUpdate: (updates: Partial<IdeaCardType>) => void;
    onDelete: () => void;
    onEntityRemove: (entityId: string) => void;
}

const FlippableCard: React.FC<FlippableCardProps> = ({
    card,
    isFlipped,
    isDraggingEntity,
    focusConfig,
    onFlip,
    onUpdate,
    onDelete,
    onEntityRemove
}) => {
    // Determine card visual state
    const isPlaced = card.instances && card.instances.length > 0;

    return (
        <div className="relative w-[90%] max-w-[900px] min-w-[400px] aspect-[16/9] max-h-full z-10" style={{ perspective: '1200px' }}>
            <div
                className="relative w-full h-full transition-transform duration-400"
                style={{
                    transformStyle: 'preserve-3d',
                    transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                }}
            >
                {/* Front: Index Card with focus stripe - Premium dark style */}
                <div
                    className={`absolute inset-0 transition-all duration-200 ${isDraggingEntity ? 'scale-[1.01]' : ''}`}
                    style={{ backfaceVisibility: 'hidden' }}
                >
                    <div
                        className="w-full h-full rounded-md overflow-hidden flex"
                        style={{
                            border: isDraggingEntity
                                ? '1px dashed rgba(119, 117, 248, 0.5)'
                                : isPlaced
                                    ? '1px solid #3F4448'
                                    : '1px dashed #3F4448'
                        }}
                    >
                        {/* Left: Focus color stripe - Subtle */}
                        <div className={`w-0.5 ${focusConfig.stripeColor} flex-shrink-0 opacity-80`} />

                        {/* Card content area */}
                        <div className={`flex-1 relative ${isPlaced ? 'bg-zinc-900' : 'bg-neutral-900'}`}>
                            {/* Subtle grid pattern */}
                            <div
                                className="absolute inset-0 opacity-[0.015]"
                                style={{
                                    backgroundImage: 'linear-gradient(to right, #3F4448 1px, transparent 1px), linear-gradient(to bottom, #3F4448 1px, transparent 1px)',
                                    backgroundSize: '16px 16px'
                                }}
                            />

                            {/* Drop zone slots when dragging */}
                            {isDraggingEntity && (
                                <div className="absolute inset-4 flex items-center justify-center gap-3 z-20">
                                    <DropSlot type="subject" label="Character" icon={<User className="w-4 h-4" />} />
                                    <DropSlot type="location" label="Location" icon={<MapPin className="w-4 h-4" />} />
                                    <DropSlot type="object" label="Prop" icon={<Package className="w-4 h-4" />} />
                                </div>
                            )}

                            {/* Actual card content */}
                            {!isDraggingEntity && (
                                <IdeaCard
                                    card={card}
                                    isSelected
                                    isStage
                                    onSelect={() => { }}
                                    onUpdate={onUpdate}
                                    onDelete={onDelete}
                                    onEntityRemove={onEntityRemove}
                                />
                            )}

                            {/* Card Action Buttons - Minimal, integrated */}
                            {!isDraggingEntity && (
                                <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5 z-30">
                                    {/* Image Button */}
                                    <button
                                        className="w-7 h-7 rounded bg-onyx border border-iron/60 flex items-center justify-center hover:bg-graphite hover:border-thing/30 transition-colors group"
                                        title="Generate Image"
                                    >
                                        <ImageIcon className="w-3.5 h-3.5 text-gray-500 group-hover:text-thing transition-colors" weight="duotone" />
                                    </button>

                                    {/* Inspector Button (flip card) */}
                                    <button
                                        onClick={onFlip}
                                        className="w-7 h-7 rounded bg-onyx border border-iron/60 flex items-center justify-center hover:bg-graphite hover:border-slate-blue/30 transition-colors group"
                                        title="Inspect Card Details"
                                    >
                                        <DotsThree className="w-3.5 h-3.5 text-gray-500 group-hover:text-slate-blue transition-colors" weight="bold" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Back: Inspector - Premium dark style */}
                <div
                    className="absolute inset-0 bg-zinc-900 rounded-md border border-iron overflow-hidden"
                    style={{
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)'
                    }}
                >
                    <CardInspector card={card} isEmbedded />

                    {/* Flip back button on inspector */}
                    <button
                        onClick={onFlip}
                        className="absolute bottom-2.5 right-2.5 w-7 h-7 rounded bg-onyx border border-iron/60 flex items-center justify-center hover:bg-graphite hover:border-slate-blue/30 transition-colors group z-30"
                        title="Back to Card"
                    >
                        <DotsThree className="w-3.5 h-3.5 text-gray-500 group-hover:text-slate-blue transition-colors" weight="bold" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DROP SLOT - Premiere style drop targets
// ═══════════════════════════════════════════════════════════════════════════════
interface DropSlotProps {
    type: 'subject' | 'location' | 'object';
    label: string;
    icon: React.ReactNode;
}

const DropSlot: React.FC<DropSlotProps> = ({ type, label, icon }) => {
    // Using muted colors from palette
    const colors = {
        subject: 'border-entity/30 text-entity-soft bg-entity/5',
        location: 'border-place/30 text-place-soft bg-place/5',
        object: 'border-thing/30 text-thing-soft bg-thing/5'
    };

    return (
        <div className={`flex flex-col items-center justify-center gap-1.5 px-5 py-3 rounded border border-dashed ${colors[type]} transition-colors hover:bg-opacity-10`}>
            {icon}
            <span className="text-[9px] font-medium">{label}</span>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EMPTY CARD STATE - Premiere style minimal
// ═══════════════════════════════════════════════════════════════════════════════
interface EmptyCardStateProps {
    onCreateCard: () => void;
    hasCards: boolean;
}

const EmptyCardState: React.FC<EmptyCardStateProps> = ({ onCreateCard, hasCards }) => {
    return (
        <div className="flex flex-col items-center justify-center text-center max-w-sm z-10">
            <div className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 bg-zinc-900 border border-iron/40">
                <Cards className="w-8 h-8 text-iron" />
            </div>
            <p className="text-sm text-gray-400 mb-5">
                {hasCards ? 'Select a card from the timeline' : onCreateCard ? 'Create your first card' : 'View cards from the timeline'}
            </p>
            {/* Show New Card button only if onCreateCard is provided */}
            {onCreateCard && (
                <button
                    onClick={onCreateCard}
                    className="flex items-center gap-2 px-4 py-2.5 rounded bg-slate-blue/15 text-slate-blue-soft hover:bg-slate-blue/25 transition-colors text-sm font-medium mb-6 border border-slate-blue/25"
                >
                    <Plus className="w-4 h-4" />
                    New Card
                </button>
            )}
            {/* Only show onboarding tips when no cards exist */}
            {!hasCards && (
                <div className="text-left text-xs text-gray-500 space-y-2.5">
                    <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded bg-graphite flex items-center justify-center text-[9px] text-gray-500 flex-shrink-0">1</span>
                        <span>Create cards to capture story beats</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded bg-graphite flex items-center justify-center text-[9px] text-gray-500 flex-shrink-0">2</span>
                        <span>Drag characters & locations onto cards</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded bg-graphite flex items-center justify-center text-[9px] text-gray-500 flex-shrink-0">3</span>
                        <span>Place cards on the timeline to structure</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PROSE VIEW - Independent story outline/prose writing (not tied to cards)
// ═══════════════════════════════════════════════════════════════════════════════
const ProseView: React.FC = () => {
    const { currentIdea, updateIdea } = useIdea();

    // Fully decoupled local state - Notion/Substack pattern
    const [localContent, setLocalContent] = useState('');
    const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
    const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const currentIdeaIdRef = React.useRef<string | null>(null);

    // Initialize content only when idea ID changes
    useEffect(() => {
        if (currentIdea?.id && currentIdea.id !== currentIdeaIdRef.current) {
            // Switching to a new idea - load its prose content
            setLocalContent(currentIdea.prose_content || '');
            currentIdeaIdRef.current = currentIdea.id;
            setSaveStatus('saved');
        } else if (!currentIdea?.id) {
            // No idea loaded
            setLocalContent('');
            currentIdeaIdRef.current = null;
            setSaveStatus('saved');
        }
    }, [currentIdea?.id]); // Only depend on idea ID, not content

    // Auto-save on idle (2 seconds after last keystroke)
    const queueSave = React.useCallback((content: string) => {
        setSaveStatus('unsaved');

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            setSaveStatus('saving');
            updateIdea({ prose_content: content });
            // Small delay to show "saving" state
            setTimeout(() => setSaveStatus('saved'), 300);
        }, 2000); // 2 second idle timeout like Notion
    }, [updateIdea]);

    // Handle content change - instant local update, queued save
    const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newContent = e.target.value;
        setLocalContent(newContent);
        queueSave(newContent);
    };

    // Save immediately on blur (leaving the textarea)
    const handleBlur = () => {
        if (saveStatus === 'unsaved' && saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            setSaveStatus('saving');
            updateIdea({ prose_content: localContent });
            setTimeout(() => setSaveStatus('saved'), 300);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    // No idea loaded state
    if (!currentIdea) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center z-10">
                <div className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 bg-zinc-900 border border-iron/40">
                    <Article className="w-8 h-8 text-iron" />
                </div>
                <p className="text-sm text-gray-400 mb-4">No project selected</p>
                <p className="text-xs text-gray-500">Select a project to start writing your story outline</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col z-10 max-w-4xl mx-auto">
            {/* Prose writing area */}
            <div className="flex-1 flex flex-col bg-zinc-900 rounded-lg border border-iron/40 overflow-hidden">
                {/* Minimal toolbar with save status */}
                <div className="flex items-center gap-2 px-4 py-2 border-b border-iron/30 bg-neutral-900">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">Story Outline</span>
                    <div className="flex-1" />
                    {/* Save status indicator - Notion style */}
                    <span className={`text-[10px] transition-colors ${
                        saveStatus === 'saving' ? 'text-thing' :
                        saveStatus === 'unsaved' ? 'text-gray-500' :
                        'text-gray-500'
                    }`}>
                        {saveStatus === 'saving' ? 'Saving...' :
                         saveStatus === 'unsaved' ? 'Editing' :
                         'Saved'}
                    </span>
                    <span className="text-iron">·</span>
                    <span className="text-[10px] text-gray-500">
                        {localContent.length.toLocaleString()} characters
                    </span>
                </div>

                {/* Writing area */}
                <textarea
                    value={localContent}
                    onChange={handleContentChange}
                    onBlur={handleBlur}
                    placeholder="Write your story outline here...

This is your dedicated space for freeform writing - plot summaries, character backstories, world-building notes, or full prose drafts.

Your outline is saved automatically and is independent of the index cards on the timeline."
                    className="flex-1 w-full p-6 bg-transparent text-neutral-200 text-sm leading-relaxed placeholder-neutral-600 outline-none resize-none font-[inherit]"
                    style={{
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        fontSize: '15px',
                        lineHeight: '1.8'
                    }}
                />
            </div>
        </div>
    );
};
